import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { disclaimersAPI } from '../../services/api';
import RichTextEditor from '../../apps/PortfolioReportsApp/RichTextEditor';
import './DisclaimersEditor.css';

/**
 * Admin > Client > Disclaimers
 *
 * Grid of brands (rows, Drummond default first) x disclaimer types (columns).
 * A brand cell is either "Uses default" (no row: the brand inherits the
 * Drummond text, so Drummond edits flow through) or an override. Cells may
 * contain placeholders resolved per report; {default} inserts the Drummond
 * text so a brand can add paragraphs without copying it.
 */

const DEFAULT_DOMAIN = '_default';

const TOKEN_HELP = {
  default: 'The Drummond default text (brand rows only). Put brand paragraphs before or after it.',
  platform_notice: "Platform wording for the portfolio's platform (table below).",
  performance_basis: "Performance source and fee basis for the portfolio's platform (Platform performance wording, below).",
  platform: "The portfolio's platform name, e.g. HUB24.",
  portfolio_name: "The brand's white-label name for the portfolio.",
  company_name: "The brand's company name.",
  inception_date: "The portfolio's inception date, d/m/yyyy.",
  inception_date_padded: "The portfolio's inception date, dd/mm/yyyy.",
  partial_year: "Performance table footers only: '*Partial year.' when the FY table has a partial year, otherwise nothing.",
};

const CATEGORY_LABELS = {
  client_report: 'Client report',
  report_note: 'Report note',
  portal_ui: 'Portal',
  tool: 'Tool',
};

const plain = (html) => (html || '')
  .replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const isBlank = (html) => !plain(html);

// Plain-text disclaimers (blank line = new paragraph) would collapse into one
// paragraph in the rich-text editor; convert them to HTML paragraphs first.
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const toEditorHtml = (text) => {
  if (!text || /<[a-z][\s\S]*>/i.test(text)) return text || '';
  return text.split(/\n\s*\n/).map((p) => `<p>${escapeHtml(p.trim()).replace(/\n/g, '<br>')}</p>`).join('');
};

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return fallback;
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

// ── Add / edit column (disclaimer type) ─────────────────────────────
const TypeForm = ({ type, categories, onSaved, onCancel }) => {
  const isNew = !type;
  const [form, setForm] = useState({
    name: type?.name || '',
    description: type?.description || '',
    category: type?.category || 'client_report',
    brand_overridable: type ? type.brand_overridable : true,
    public: type ? type.public : false,
    archived: type ? type.archived : false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const { archived, ...payload } = form;
        const t = await disclaimersAPI.createType(payload);
        setCreated(t);
        onSaved(t, false);
      } else {
        const t = await disclaimersAPI.updateType(type.key, form);
        onSaved(t, true);
      }
    } catch (err) {
      setError(errText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div className="disclaimers-panel">
        <h5>Column added: {created.name}</h5>
        <p className="mb-1">It is available at this endpoint. It returns the text resolved for the caller's brand:</p>
        <p>
          <span className="disclaimers-endpoint">GET {created.endpoint}?portfolio_code=&lt;code&gt;</span>
        </p>
        <p className="mb-2">Set its Drummond default text in the grid before using it, or the endpoint returns an error.</p>
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Close</button>
      </div>
    );
  }

  return (
    <div className="disclaimers-panel">
      <h5>{isNew ? 'Add disclaimer column' : `Column: ${type.name}`}</h5>
      {!isNew && (
        <p className="small mb-2">
          Endpoint: <span className="disclaimers-endpoint">GET {type.endpoint}</span>
        </p>
      )}
      <div className="row g-2">
        <div className="col-md-5">
          <label className="form-label small mb-0">Name</label>
          <input className="form-control form-control-sm" value={form.name}
                 onChange={(e) => set('name', e.target.value)} placeholder="e.g. Quarterly portfolio report" />
        </div>
        <div className="col-md-3">
          <label className="form-label small mb-0">Category</label>
          <select className="form-select form-select-sm" value={form.category}
                  onChange={(e) => set('category', e.target.value)}>
            {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </div>
        <div className="col-md-12">
          <label className="form-label small mb-0">Description</label>
          <input className="form-control form-control-sm" value={form.description || ''}
                 onChange={(e) => set('description', e.target.value)}
                 placeholder="Where this disclaimer appears" />
        </div>
        <div className="col-md-12 d-flex gap-4 small">
          <label><input type="checkbox" checked={form.brand_overridable}
                        onChange={(e) => set('brand_overridable', e.target.checked)} /> Brands can override</label>
          <label><input type="checkbox" checked={form.public}
                        onChange={(e) => set('public', e.target.checked)} /> Readable before login</label>
          {!isNew && (
            <label><input type="checkbox" checked={form.archived}
                          onChange={(e) => set('archived', e.target.checked)} /> Archived (hidden, endpoint off)</label>
          )}
        </div>
      </div>
      {error && <div className="text-danger small mt-2">{error}</div>}
      <div className="mt-2 d-flex gap-2">
        <button className="btn btn-sm btn-primary" disabled={saving || !form.name.trim()} onClick={save}>
          {saving ? 'Saving…' : isNew ? 'Add column' : 'Save column'}
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

// ── Cell editor ─────────────────────────────────────────────────────
const CellPanel = ({ brand, type, cell, defaultCell, tokens, onSaved, onClose }) => {
  const isDefaultRow = brand.domain === DEFAULT_DOMAIN;
  const hasOverride = !!cell;
  const [overriding, setOverriding] = useState(isDefaultRow || hasOverride);
  const [draft, setDraft] = useState(toEditorHtml(cell?.text) || (isDefaultRow ? '' : '<p>{default}</p>'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [previewCode, setPreviewCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState(null);

  const saved = toEditorHtml(cell?.text || '');
  const dirty = overriding && draft !== saved && !(isBlank(draft) && !hasOverride && !isDefaultRow);

  const loadHistory = useCallback(() => {
    disclaimersAPI.history({ domain: brand.domain, type_key: type.key })
      .then(setHistory)
      .catch((err) => setHistory({ error: errText(err, 'Could not load history') }));
  }, [brand.domain, type.key]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runPreview = async () => {
    setPreview({ loading: true });
    try {
      const r = await disclaimersAPI.preview(type.key, brand.domain, previewCode.trim() || undefined);
      setPreview(r);
    } catch (err) {
      setPreview({ error: errText(err, 'Preview failed') });
    }
  };

  const save = async (text) => {
    setSaving(true);
    setError(null);
    try {
      await disclaimersAPI.saveCell(brand.domain, type.key, text);
      await onSaved();
      loadHistory();
      if (preview && !preview.loading) runPreview();
    } catch (err) {
      setError(errText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm(`Remove ${brand.company_name}'s override? They will use the Drummond default again.`)) return;
    await save('');
    setOverriding(false);
    setDraft('<p>{default}</p>');
  };

  const copyToken = async (tok) => {
    try {
      await navigator.clipboard.writeText(`{${tok}}`);
      setCopied(tok);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  const visibleTokens = tokens.filter((t) => t !== 'default' || !isDefaultRow);

  return (
    <div className="disclaimers-panel">
      <div className="d-flex align-items-start justify-content-between">
        <div>
          <h5 className="mb-0">{brand.company_name} · {type.name}</h5>
          <div className="small text-muted">
            {isDefaultRow ? 'Drummond default: every brand without an override uses this.'
              : hasOverride ? `Override, last saved by ${cell.updated_by || 'unknown'} ${fmtWhen(cell.updated_at)}`
                : 'Uses the Drummond default.'}
          </div>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
      </div>

      <div className="row mt-3">
        <div className="col-lg-7">
          {!overriding ? (
            <>
              <div className="disclaimers-preview mb-2"
                   dangerouslySetInnerHTML={{ __html: defaultCell?.text || '<em>No Drummond default set yet.</em>' }} />
              <button className="btn btn-sm btn-primary" onClick={() => setOverriding(true)}>
                Override for {brand.company_name}
              </button>
              <div className="small text-muted mt-1">
                The override starts as {'{default}'}, so you only add the brand's extra paragraphs and Drummond
                edits keep flowing through. Delete {'{default}'} to replace the text entirely.
              </div>
            </>
          ) : (
            <>
              <div className="mb-2">
                {visibleTokens.map((tok) => (
                  <span key={tok} className="token-chip" title={TOKEN_HELP[tok] || ''} onClick={() => copyToken(tok)}>
                    {copied === tok ? 'Copied' : `{${tok}}`}
                  </span>
                ))}
                <div className="small text-muted">Click a placeholder to copy it, then paste it into the text.</div>
              </div>
              <RichTextEditor value={draft} onChange={setDraft} placeholder="Disclaimer text…" />
              {error && <div className="text-danger small mt-2">{error}</div>}
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <button className="btn btn-sm btn-primary" disabled={saving || !dirty || (isDefaultRow && isBlank(draft))}
                        onClick={() => save(draft)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-sm btn-outline-secondary" disabled={!dirty}
                        onClick={() => setDraft(saved || (isDefaultRow ? '' : '<p>{default}</p>'))}>
                  Discard changes
                </button>
                {!isDefaultRow && hasOverride && (
                  <button className="btn btn-sm btn-outline-danger ms-auto" disabled={saving} onClick={clearOverride}>
                    Remove override (use default)
                  </button>
                )}
                {!isDefaultRow && !hasOverride && (
                  <button className="btn btn-sm btn-link" onClick={() => { setOverriding(false); setDraft('<p>{default}</p>'); }}>
                    Cancel override
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="col-lg-5">
          <label className="form-label small mb-1 fw-bold">Preview as {brand.company_name}</label>
          <div className="d-flex gap-2 mb-2">
            <input className="form-control form-control-sm" placeholder="Portfolio code (e.g. DER003)"
                   value={previewCode} onChange={(e) => setPreviewCode(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') runPreview(); }} />
            <button className="btn btn-sm btn-outline-primary" onClick={runPreview}>Preview</button>
          </div>
          <div className="small text-muted mb-1">Preview shows saved text. Save first to preview changes.</div>
          {preview?.loading && <div className="small">Loading…</div>}
          {preview?.error && <div className="text-danger small">{preview.error}</div>}
          {preview?.text !== undefined && (
            <>
              {preview.platform && <div className="small mb-1">Platform: <strong>{preview.platform}</strong></div>}
              <div className="disclaimers-preview" dangerouslySetInnerHTML={{ __html: preview.text }} />
            </>
          )}

          <label className="form-label small mb-1 mt-3 fw-bold">History</label>
          <div className="disclaimers-history">
            {history === null && <div>Loading…</div>}
            {history?.error && <div className="text-danger">{history.error}</div>}
            {Array.isArray(history) && history.length === 0 && <div className="text-muted">No changes yet.</div>}
            {Array.isArray(history) && history.map((h) => (
              <div key={h.id} className="hist-row">
                <div>
                  <strong>{h.new_text === null ? 'Override removed' : h.old_text === null ? 'Created' : 'Edited'}</strong>
                  {' '}by {h.saved_by} · {fmtWhen(h.saved_at)}
                </div>
                {h.old_text && (
                  <div className="text-muted">
                    Before: {plain(h.old_text).slice(0, 140)}{plain(h.old_text).length > 140 ? '…' : ''}{' '}
                    <button className="btn btn-sm btn-link p-0 align-baseline"
                            onClick={() => { setOverriding(true); setDraft(h.old_text); }}>
                      Load into editor
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Platform notice editor ──────────────────────────────────────────
const PlatformNoticeRow = ({ notice, onSaved, saveFn = disclaimersAPI.savePlatformNotice,
  placeholder = 'Sentence(s) inserted where {platform_notice} appears. Leave blank to remove.',
  emptyText = 'Not set. Reports for this platform show a visible "No platform notice" marker.' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notice.text || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveFn(notice.platform, draft);
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError(errText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <th style={{ width: 160 }}>{notice.platform}</th>
      <td>
        {editing ? (
          <>
            <textarea className="form-control form-control-sm" rows={3} value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={placeholder} />
            {error && <div className="text-danger small">{error}</div>}
            <div className="mt-1 d-flex gap-2">
              <button className="btn btn-sm btn-primary" disabled={saving || draft === (notice.text || '')} onClick={save}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-sm btn-outline-secondary"
                      onClick={() => { setDraft(notice.text || ''); setEditing(false); }}>Cancel</button>
            </div>
          </>
        ) : (
          <div style={{ cursor: 'pointer' }} onClick={() => setEditing(true)}>
            {notice.text
              ? notice.text
              : <span className="text-danger">{emptyText}</span>}
            {notice.updated_by && (
              <div className="small text-muted">Saved by {notice.updated_by} {fmtWhen(notice.updated_at)}</div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

// ── Main ────────────────────────────────────────────────────────────
const DisclaimersEditor = () => {
  const [grid, setGrid] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // {domain, typeKey}
  const [typeEditor, setTypeEditor] = useState(null); // null | 'new' | type
  const [extraBrands, setExtraBrands] = useState([]);
  const [newBrand, setNewBrand] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // Hide brands with no overrides: they follow the Drummond default everywhere.
  const [hideDefaults, setHideDefaults] = useState(true);
  const [brandFilter, setBrandFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setGrid(await disclaimersAPI.getGrid());
      setError(null);
    } catch (err) {
      setError(errText(err, 'Could not load disclaimers'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cellMap = useMemo(() => {
    const m = {};
    (grid?.cells || []).forEach((c) => { m[`${c.domain}|${c.type_key}`] = c; });
    return m;
  }, [grid]);

  const types = useMemo(
    () => (grid?.types || []).filter((t) => showArchived || !t.archived),
    [grid, showArchived],
  );

  const brands = useMemo(() => {
    if (!grid) return [];
    const known = new Set(grid.brands.map((b) => b.domain));
    const extra = extraBrands.filter((d) => !known.has(d)).map((d) => ({ domain: d, company_name: d }));
    const overridden = new Set(grid.cells.map((c) => c.domain));
    const f = brandFilter.trim().toLowerCase();
    return [...grid.brands, ...extra].filter((b) => {
      if (b.domain === DEFAULT_DOMAIN) return true;
      // Rows added by hand, and the row being edited, stay visible.
      const pinned = extraBrands.includes(b.domain) || selected?.domain === b.domain;
      if (hideDefaults && !pinned && !overridden.has(b.domain)) return false;
      return !f || b.domain.includes(f) || (b.company_name || '').toLowerCase().includes(f);
    });
  }, [grid, extraBrands, brandFilter, hideDefaults, selected]);

  const hiddenCount = useMemo(() => {
    if (!grid || !hideDefaults) return 0;
    const overridden = new Set(grid.cells.map((c) => c.domain));
    return grid.brands.filter((b) => b.domain !== DEFAULT_DOMAIN && !overridden.has(b.domain)).length;
  }, [grid, hideDefaults]);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!grid) return <div>Loading disclaimers…</div>;

  const selBrand = selected && brands.find((b) => b.domain === selected.domain);
  const selType = selected && grid.types.find((t) => t.key === selected.typeKey);

  const addBrand = () => {
    const d = newBrand.trim().toLowerCase();
    if (!d || d.includes(' ') || !d.includes('.')) return;
    setExtraBrands((x) => [...x, d]);
    setNewBrand('');
  };

  return (
    <div className="disclaimers-editor">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="mb-0">Disclaimers</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={() => { setSelected(null); setTypeEditor('new'); }}>
            + Add disclaimer column
          </button>
        </div>
      </div>
      <div className="disclaimers-note mb-3">
        Each column is one disclaimer, served by its own endpoint. The <strong>Drummond default</strong> row applies to
        every brand. A brand cell marked <em>Uses default</em> follows the Drummond text automatically. Only override where
        a brand genuinely needs different wording, and start from {'{default}'} so Drummond changes still reach them.
        Placeholders such as {'{platform_notice}'} and {'{portfolio_name}'} are filled in per report.
      </div>

      <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
        <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Filter brands"
               value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} />
        <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Add brand row (domain)"
               value={newBrand} onChange={(e) => setNewBrand(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') addBrand(); }} />
        <button className="btn btn-sm btn-outline-secondary" onClick={addBrand}>Add row</button>
        <label className="small ms-auto">
          <input type="checkbox" checked={hideDefaults} onChange={(e) => setHideDefaults(e.target.checked)} />
          {' '}Hide brands using defaults{hideDefaults && hiddenCount ? ` (${hiddenCount} hidden)` : ''}
        </label>
        <label className="small">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived columns
        </label>
      </div>

      <div className="mb-3">
      {typeEditor && (
        <TypeForm
          key={typeEditor === 'new' ? 'new' : typeEditor.key}
          type={typeEditor === 'new' ? null : typeEditor}
          categories={grid.categories}
          onSaved={async (t, close) => { await load(); if (close) setTypeEditor(null); }}
          onCancel={() => setTypeEditor(null)}
        />
      )}

      {selBrand && selType && (
        <CellPanel
          key={`${selBrand.domain}|${selType.key}`}
          brand={selBrand}
          type={selType}
          cell={cellMap[`${selBrand.domain}|${selType.key}`]}
          defaultCell={cellMap[`${DEFAULT_DOMAIN}|${selType.key}`]}
          tokens={grid.tokens}
          onSaved={load}
          onClose={() => setSelected(null)}
        />
      )}
      </div>

      <div className="disclaimers-grid-wrap">
        <table className="disclaimers-grid">
          <thead>
            <tr>
              <th style={{ left: 0, zIndex: 2 }}>Brand</th>
              {types.map((t) => (
                <th key={t.key} className={`cat-${t.category}`} style={{ cursor: 'pointer' }}
                    title="Edit column" onClick={() => { setSelected(null); setTypeEditor(t); }}>
                  {t.name}{t.archived ? ' (archived)' : ''}
                  <span className="col-key">{t.key} · {CATEGORY_LABELS[t.category] || t.category}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {brands.map((b) => {
              const isDefault = b.domain === DEFAULT_DOMAIN;
              return (
                <tr key={b.domain} className={isDefault ? 'row-default' : ''}>
                  <th>
                    {b.company_name}
                    {!isDefault && <div className="small text-muted fw-normal">{b.domain}</div>}
                  </th>
                  {types.map((t) => {
                    const cell = cellMap[`${b.domain}|${t.key}`];
                    const locked = !isDefault && !t.brand_overridable;
                    const isSel = selected?.domain === b.domain && selected?.typeKey === t.key;
                    const cls = ['cell', locked ? 'locked' : '', !isDefault && !cell && !locked ? 'inherits' : '',
                      isSel ? 'selected' : ''].join(' ');
                    let content;
                    if (locked) content = 'Same for all brands';
                    else if (cell) {
                      content = (
                        <>
                          {!isDefault && <span className="badge-override">Override</span>}
                          <div className="excerpt">{plain(cell.text)}</div>
                        </>
                      );
                    } else if (isDefault) content = <span className="text-danger">Not set</span>;
                    else content = <div className="excerpt">Uses default</div>;
                    return (
                      <td key={t.key} className={cls}
                          onClick={() => { if (!locked) { setTypeEditor(null); setSelected({ domain: b.domain, typeKey: t.key }); } }}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h5 className="mt-4" style={{ color: '#012341', fontWeight: 300 }}>Platform notices</h5>
      <p className="small text-muted mb-2">
        Inserted wherever a disclaimer contains {'{platform_notice}'}, using the portfolio's platform from Shadow_Data
        (or its declared platform if it has no holdings yet). Click a row to edit.
      </p>
      <div className="disclaimers-grid-wrap">
        <table className="disclaimers-grid">
          <tbody>
            {grid.platform_notices.map((n) => (
              <PlatformNoticeRow key={n.platform} notice={n} onSaved={load} />
            ))}
          </tbody>
        </table>
      </div>

      <h5 className="mt-4" style={{ color: '#012341', fontWeight: 300 }}>Platform performance wording</h5>
      <p className="small text-muted mb-2">
        Inserted wherever a disclaimer contains {'{performance_basis}'}: the source and fee basis at the start of the
        performance table footer on fact sheets and quarterly reports. A platform left blank uses the generic
        performance footer instead. A portfolio with its own footer on its fact sheet keeps that wording.
      </p>
      <div className="disclaimers-grid-wrap">
        <table className="disclaimers-grid">
          <tbody>
            {(grid.platform_performance || []).map((n) => (
              <PlatformNoticeRow key={n.platform} notice={n} onSaved={load}
                saveFn={disclaimersAPI.savePlatformPerformance}
                placeholder="e.g. Source: HUB24. Portfolio performance is before … Leave blank to use the generic footer."
                emptyText="Not set. Portfolios on this platform use the generic performance footer." />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DisclaimersEditor;
